import { CompactNumber } from "@/shared/ui/CompactNumber";
import { Card, CardContent } from "@/components/ui/card";
import {
  CheckCircle2Icon,
  ClockIcon,
  CoinsIcon,
  ListTodoIcon,
} from "lucide-react";

type RefundQueueSummaryCardsProps = {
  totalPendingRequests: number;
  totalPendingAmount: number;
  avgResponseTimeMinutes: number;
  autoApproveToday: number;
  isLoading?: boolean;
};

export function RefundQueueSummaryCards({
  totalPendingRequests,
  totalPendingAmount,
  avgResponseTimeMinutes,
  autoApproveToday,
  isLoading,
}: RefundQueueSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="rounded-xl border bg-card">
            <CardContent>
              <div className="h-10 animate-pulse rounded bg-muted" />
              <div className="mt-2 h-6 w-16 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Card className="rounded-xl border border-amber-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
            <ListTodoIcon className="size-5 text-amber-600" />
          </div>
          <div>
            <p className="text-muted-foreground text-sm">
              ລາຍການລໍຖ້າທັງໝົດ
            </p>
            <p className="font-semibold text-2xl tabular-nums">
              <CompactNumber value={totalPendingRequests} />
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border border-blue-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
            <CoinsIcon className="size-5 text-blue-600" />
          </div>
          <div>
            <p className="text-muted-foreground text-sm">
              ຍອດລໍຖ້າລວມ (LAK)
            </p>
            <p className="font-semibold text-2xl tabular-nums">
              <CompactNumber
                value={totalPendingAmount}
                formatOptions={{
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }}
              />
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border border-violet-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-violet-500/10">
            <ClockIcon className="size-5 text-violet-600" />
          </div>
          <div>
            <p className="text-muted-foreground text-sm">
              ເວລາຕອບສະໜອງສະເລ່ຍ (ນາທີ)
            </p>
            <p className="font-semibold text-2xl tabular-nums">
              <CompactNumber
                value={avgResponseTimeMinutes}
                formatOptions={{
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                }}
              />
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border border-emerald-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10">
            <CheckCircle2Icon className="size-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-muted-foreground text-sm">
              ອະນຸມັດມື້ນີ້
            </p>
            <p className="font-semibold text-2xl tabular-nums">
              <CompactNumber value={autoApproveToday} />
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
